import React, { useState, useEffect, useRef } from "react";

import { Dialog, Card } from "@mui/material";
import { images, icons, FLAVOR, MAIL_FORMATTING } from "../../helpers/constants";
import { useTranslation } from "react-i18next";
import Button from "../Buttons/Button";
import { useIsMobile } from "../../helpers/customHooks";
import SegmentedControl from "../SegmentedControl/SegmentedControl";
import NewButton from "../Buttons/NewButton";
import DeviceSelectionRow from "../Devices/DeviceSelectionRow";
import toast from "react-hot-toast";
import DeviceSelectionRowNew from "../Devices/DeviceSelectionRowNew";

const AddDeviceModal = ({ show, sharableDevices, type, onClose, onSave, deleteAccount }) => {
	const MOBILE = useIsMobile();
	const { t } = useTranslation();
	const inputFile = useRef(null);

	// const [show, setShow] = useState(props.show)
	const [devicename, setDevicename] = useState("");
	const [idnumber, setIdnumber] = useState("");
	const [errorText, setErrorText] = useState("");
	const [devices, setDevices] = useState([]);
	const [allSelected, setAllSelected] = useState(false);
	const [email, setEmail] = useState("");
	const [addedEmails, setAddedEmails] = useState([]);

	const handleClose = () => {
		onClose();
		setDevicename("");
		setIdnumber("");
		setDevices([]);
		setAllSelected(false);
		setAddedEmails([]);
	};
	useEffect(() => {
		if (sharableDevices) {
			setDevices(sharableDevices);
		}
	}, [sharableDevices, show]);

	const handleChange = (type, e) => {
		switch (type) {
			case "devicename":
				setDevicename(e.target.value);
				break;
			case "idnumber":
				setIdnumber(e.target.value);
				break;
			case "email":
				setEmail(e.target.value);
			default:
				return;
		}
		setErrorText("");
	};

	const handleSave = (type) => {
		let data = {};
		switch (type) {
			case "add_device":
				data = { name: devicename, regcode: idnumber };
				break;
			case "share_device":
				const selectedIds = devices?.filter((device) => device.selected).map((device) => device.id);
				if (selectedIds.length === 0) {
					toast.error(t("settings.pick_one_device"));
					return;
				}
				if (addedEmails.length === 0) {
					toast.error(t("settings.enter_email"));
					return;
				}
				data = { deviceid: selectedIds, email: addedEmails };
				break;
			default:
				data = {};
		}
		onSave(data);
	};
	const getCount = (d) => d?.shared_permissions?.sharedto_count ?? 0;
	const getLimit = (d) => d?.shared_permissions?.sharedlimit ?? Number.POSITIVE_INFINITY;

	const underLimit = (d) => getCount(d) < getLimit(d);
	const isAdmin = (d) => !!d.admin ?? false;

	// "Eligible" for bulk select = admin and under limit
	const eligibleForBulk = (d) => isAdmin(d) && underLimit(d);

	// Header "choose all" selected state (true only if *all eligible admin* devices are selected)
	const allEligibleSelected = /*(devices ?? []).filter(eligibleForBulk).length > 0 && */ (devices ?? []).filter(eligibleForBulk).every((d) => d.selected);

	const selectDevice = (deviceId) => {
		setDevices((prev) => prev.map((d) => (d.id === deviceId ? { ...d, selected: !d.selected } : d)));
	};
	const canToggle = (d) => {
		if (!isAdmin(d)) return false;

		// Always allow unselect
		if (d.selected) return true;

		// Only allow selecting if under limit and selectable (default true)
		return (d.selectable ?? true) && underLimit(d);
	};
	// Bulk select / unselect via header
	useEffect(() => {
		setDevices((prev) => {
			if (!Array.isArray(prev) || prev.length === 0) return prev;

			if (allSelected) {
				// Select only devices that are eligible to be newly selected
				return prev.map((d) => {
					if (!isAdmin(d)) return d;

					// If already selected, keep it selected
					if (d.selected) return d;

					// Only select if it can be selected
					return canToggle(d) ? { ...d, selected: true } : d;
				});
			}

			// Unselect all admin devices (or only eligible ones if you prefer)
			return prev.map((d) => (eligibleForBulk(d) ? { ...d, selected: false } : d));
		});
	}, [allSelected]);

	const removeEmail = (emailToRemove) => {
		setAddedEmails((prev) => prev.filter((email) => email !== emailToRemove));
	};

	const addContent = () => {
		return (
			<div className="p-3">
				<div className="d-flex flex-row justify-content-between align-items-center">
					<div className="d-flex align-items-center">
						{/* <img src={icons.user_settings} alt=""/> */}
						<h3 className="">{t("settings.add_device")}</h3>
					</div>
					<img className="cursor-pointer" src={icons.close_gray} onClick={handleClose} alt="..." />
				</div>
				<p className="grey-blue py-3">{t("settings.add_device_sub")}</p>
				<form>
					<div className="form-group d-flex align-items-center">
						<input
							type="text"
							className="form-control rounded24 p-3 bg-light-grey grey-blue mb-3"
							id="name"
							placeholder={t("settings.device_name")}
							defaultValue={devicename}
							onChange={(e) => {
								handleChange("devicename", e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault(); // Prevent the default Enter key behavior
									document.getElementById("idnumber").focus();
								}
							}}
						/>
						<div className="invalid-feedback ms-1 bottom">{errorText === "" ? "" : errorText}</div>
					</div>
					<div className="form-group d-flex align-items-center">
						<input
							type="text"
							className="form-control rounded24 p-3 bg-light-grey grey-blue"
							id="idnumber"
							placeholder={t("settings.id_number")}
							defaultValue={idnumber}
							onChange={(e) => {
								handleChange("idnumber", e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault(); // Prevent the default Enter key behavior
									handleSave(type);
								}
							}}
						/>
						<div className="invalid-feedback ms-1 bottom">{errorText === "" ? "" : errorText}</div>
					</div>
				</form>
				<div className="d-flex flex-row justify-content-between align-items-center mt-4">
					<NewButton onClick={handleClose} className="bg-grey p-3 rounded24 w-100 dark-grey me-2 body bold">
						{t("settings.cancel")}
					</NewButton>
					<NewButton onClick={() => handleSave(type)} className="bg-gradient p-3 rounded24 w-100 white-white ms-2 body bold">
						{t("settings.save")}
					</NewButton>
				</div>
			</div>
		);
	};

	const shareContent = () => {
		return (
			<div className="p-3">
				<div className="d-flex flex-row justify-content-between align-items-center">
					<div className="d-flex align-items-center">
						{/* <img src={icons.user_settings} alt=""/> */}
						<h3 className="">{t("settings.share_device")}</h3>
					</div>
					<img className="cursor-pointer" src={icons.close_gray} onClick={handleClose} alt="..." />
				</div>
				<p className="grey-blue py-3">{t("settings.share_device_sub")}</p>
				<div className={`share_device_list scrollable-y rounded24 bg-light-grey mb-3`}>
					<DeviceSelectionRowNew text={t("settings.choose_all")} selected={allEligibleSelected} onClick={() => setAllSelected((v) => !v)} dividerColor="bg-grey" />

					{(devices ?? []).filter(isAdmin).map((device, index, arr) => {
						const count = getCount(device);
						const limit = getLimit(device);
						const info_text = device?.shared_permissions?.info_message ?? t("toast.share_device_reached_limit");

						const clickable = canToggle(device);

						return (
							<DeviceSelectionRowNew
								last={index === arr.length - 1}
								key={device.id}
								index={device.id}
								text={device.name}
								subtitle={t("settings.devices_shared", { val1: count, val2: limit })}
								selectable={clickable}
								selected={!!device.selected}
								onClick={() => (clickable ? selectDevice(device.id) : toast(info_text, { icon: <img className="size_20" src={icons.info_icon} alt="" /> }))}
								dividerColor="bg-grey"
							/>
						);
					})}
				</div>
				<div className={`d-flex flex-column rounded24 px-2 py-3 bg-light-grey ${addedEmails.length === 0 ? "d-none" : ""} mb-3`}>
					{addedEmails.map((email, index) => (
						<div key={index} className={`d-flex justify-content-between align-items-center p-2 rounded12 ${index !== addedEmails.length - 1 ? "mb-2" : ""}`}>
							<p className="">{email}</p>
							<NewButton notext={true} className="full-rounded bg-grey p-2" icon={icons.close} iconClass={"dark-grey-icon size_12"} onClick={() => removeEmail(email)} />
						</div>
					))}
				</div>
				<form>
					<div className="form-group d-flex align-items-center">
						<input
							type="text"
							className="form-control rounded24 p-3 bg-light-grey grey-blue"
							id="email"
							placeholder={t("settings.email")}
							value={email}
							onChange={(e) => {
								handleChange("email", e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									if (!MAIL_FORMATTING.test(e.target.value) || addedEmails.includes(e.target.value)) {
										toast.error(t("settings.error_adding_email"));
									} else {
										const newAddedEmails = [...addedEmails, e.target.value];
										setAddedEmails(newAddedEmails); // Update the state with the new array
										setEmail("");
									}
									e.preventDefault(); // Prevent the default Enter key behavior
								}
							}}
						/>
					</div>
				</form>
				<div className="d-flex flex-row justify-content-between align-items-center mt-4">
					<NewButton onClick={handleClose} className="bg-grey p-3 rounded24 w-100 dark-grey me-2 body bold">
						{t("settings.cancel")}
					</NewButton>
					<NewButton onClick={() => handleSave(type)} className="bg-gradient p-3 rounded24 w-100 white-white ms-2 body bold">
						{t("settings.save")}
					</NewButton>
				</div>
			</div>
		);
	};

	const typeRender = () => {
		switch (type) {
			case "add_device":
				return addContent();
			case "share_device":
				return shareContent();

			default:
				return null;
		}
	};

	return (
		<>
			<Dialog fullWidth={MOBILE ? true : false} open={show} onClose={handleClose}>
				<Card className="settings-dialog d-flex flex-column scrollable-y">
					<div className="w-100 h-100 grey-blue">{typeRender()}</div>
				</Card>
			</Dialog>
		</>
	);
};

export default AddDeviceModal;
